import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  PROXY_SAVE_FAILED_MESSAGE,
  QA_TARGETING_ALL_ASNS,
  QA_TARGETING_ALL_CITIES,
  QA_TARGETING_ALL_STATES,
  QA_TARGETING_ALL_ZIPS,
  QA_TARGETING_MODE_DEFAULT,
  QA_TARGETING_MODE_FILTERS,
  TARGETED_TRAFFIC_COST_NOTICE,
  QA_BROWSER_DISPLAY_CURRENT,
  QA_BROWSER_DISPLAY_OPTIONS,
  QA_LOCATION_SOURCE_DEFAULT,
  QA_LOCATION_SOURCE_IDENTITY,
  QA_LOCATION_SOURCE_MANUAL,
  formatQaLocationSourceLabel,
  formatQaTestLocationSummary,
  formatCompactQaLocationLabel,
  formatSuggestionDistanceHint,
  formatUsStateLabel,
  formatWebAccessModeLabel,
  normalizeQaBrowserDisplay,
  getAuthorizedQaProxy,
  getAuthorizedQaProxyLocationFromIdentity,
  getAuthorizedQaProxyLocationOptions,
  getAuthorizedQaProxyNearbyLocations,
  getAuthorizedQaProxyTargetingCatalog,
  normalizeAuthorizedQaProxySummary,
  normalizeTargetingCatalogPayload,
  searchAuthorizedQaProxyTargetingAsns,
  searchAuthorizedQaProxyTargetingCities,
  searchAuthorizedQaProxyTargetingStates,
  searchAuthorizedQaProxyTargetingZips,
  upsertAuthorizedQaProxy,
  setAuthorizedQaProxyProvider,
  formatQaProxyProviderLabel,
  AUTHORIZED_QA_PROXY_PROVIDERS,
  QA_PROXY_PROVIDER_911PROXY,
  QA_PROXY_PROVIDER_SWITCH_MESSAGE,
} from '../../../services/funding/fundingAuthorizedQaProxyService';
import QaTargetingCatalogSearch from './QaTargetingCatalogSearch';

const EMPTY_FORM = {
  provider: 'dataimpulse',
  quotaBytes: '',
  status: 'active',
  targetingMode: QA_TARGETING_MODE_DEFAULT,
  targetingCountry: 'us',
  targetingState: QA_TARGETING_ALL_STATES,
  targetingCity: QA_TARGETING_ALL_CITIES,
  targetingZip: QA_TARGETING_ALL_ZIPS,
  targetingAsn: QA_TARGETING_ALL_ASNS,
  qaBrowserDisplay: QA_BROWSER_DISPLAY_CURRENT,
  qaLocationSource: QA_LOCATION_SOURCE_DEFAULT,
};

function normalizeTargetingCountry(value) {
  const raw = String(value || 'us').trim().toLowerCase();
  if (
    !raw
    || raw === 'us'
    || raw === 'usa'
    || raw === 'united states'
    || raw === 'united-states'
  ) {
    return 'us';
  }
  return raw;
}

function normalizeTargetingAsn(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw || raw === 'all' || raw === QA_TARGETING_ALL_ASNS) {
    return QA_TARGETING_ALL_ASNS;
  }
  return String(value).trim();
}

function formFromSummary(proxySummary) {
  const summary = normalizeAuthorizedQaProxySummary(proxySummary) || {};
  const selectionRequired = summary.providerTargetReady === false
    || String(summary.providerTargetStatus || '').toLowerCase() === 'selection_required'
    || Boolean(summary.locationSelectionRequired?.required);
  const targeting = selectionRequired
    ? (summary.locationSelectionRequired?.requestedTargeting
      || summary.qaRequestedTargeting
      || summary.targeting
      || {})
    : (summary.targeting || {});
  const hasFilters = targeting.mode === QA_TARGETING_MODE_FILTERS
    || Boolean(targeting.usesTargetFilters)
    || (
      (targeting.state && targeting.state !== QA_TARGETING_ALL_STATES)
      || (targeting.city && targeting.city !== QA_TARGETING_ALL_CITIES)
      || (targeting.zip && targeting.zip !== QA_TARGETING_ALL_ZIPS)
      || (targeting.asn && targeting.asn !== QA_TARGETING_ALL_ASNS)
    );
  return {
    provider: summary.defaultProvider || 'dataimpulse',
    quotaBytes: summary.quotaBytes != null ? String(summary.quotaBytes) : '',
    status: summary.status === 'suspended' ? 'suspended' : 'active',
    targetingMode: hasFilters ? QA_TARGETING_MODE_FILTERS : QA_TARGETING_MODE_DEFAULT,
    targetingCountry: normalizeTargetingCountry(targeting.country),
    targetingState: targeting.state || QA_TARGETING_ALL_STATES,
    targetingCity: targeting.city || QA_TARGETING_ALL_CITIES,
    targetingZip: targeting.zip || QA_TARGETING_ALL_ZIPS,
    targetingAsn: normalizeTargetingAsn(targeting.asn),
    qaBrowserDisplay: normalizeQaBrowserDisplay(summary.qaBrowserDisplay),
    qaLocationSource: summary.qaLocationSource || summary.locationSource || QA_LOCATION_SOURCE_DEFAULT,
  };
}

function zipMetaUnsupported(zipMeta, zipCode) {
  const code = String(zipCode || '').trim();
  const specific = code && code !== QA_TARGETING_ALL_ZIPS && code.toLowerCase() !== 'all';
  if (!specific) return false;
  if (zipMeta?.providerSupported === false) return true;
  if (Number.isFinite(Number(zipMeta?.availableIpCount)) && Number(zipMeta.availableIpCount) <= 0) {
    return true;
  }
  return false;
}

function shouldPrefillFromIdentity(summary) {
  const source = String(
    summary?.qaLocationSource || summary?.locationSource || '',
  ).trim().toLowerCase();
  if (source === QA_LOCATION_SOURCE_MANUAL) return false;
  if (
    source === QA_LOCATION_SOURCE_IDENTITY
    || !source
    || source === QA_LOCATION_SOURCE_DEFAULT
  ) {
    return true;
  }
  const targeting = summary?.targeting || {};
  return targeting.mode !== QA_TARGETING_MODE_FILTERS;
}

function applyIdentityPrefill(prefill, setForm, setSelectedMeta) {
  if (!prefill || typeof prefill !== 'object') {
    return { unsupportedZip: false, zip: null, formSnapshot: null };
  }
  const state = prefill.state
    ? String(prefill.state)
    : QA_TARGETING_ALL_STATES;
  const city = prefill.city
    ? String(prefill.city).toLowerCase()
    : QA_TARGETING_ALL_CITIES;
  const zip = prefill.zip ? String(prefill.zip) : QA_TARGETING_ALL_ZIPS;
  const hasFilters = (
    state !== QA_TARGETING_ALL_STATES
    || city !== QA_TARGETING_ALL_CITIES
    || zip !== QA_TARGETING_ALL_ZIPS
  );
  const country = normalizeTargetingCountry(
    prefill.country || prefill.countryLabel || prefill.parsed?.country,
  );
  const catalogMatch = prefill.catalogMatch || prefill.catalog_match || null;
  const zipIpCount = catalogMatch?.kind === 'zip'
    ? (catalogMatch.availableIpCount ?? catalogMatch.available_ip_count)
    : (prefill.zipAvailableIpCount ?? prefill.zip_available_ip_count);
  const zipSupported = zip !== QA_TARGETING_ALL_ZIPS
    ? (
      catalogMatch?.providerSupported
      ?? catalogMatch?.provider_supported
      ?? (zipIpCount == null ? null : Number(zipIpCount) > 0)
    )
    : true;
  const unsupportedZip = zip !== QA_TARGETING_ALL_ZIPS
    && zipSupported === false;
  const formSnapshot = {
    targetingMode: hasFilters ? QA_TARGETING_MODE_FILTERS : QA_TARGETING_MODE_DEFAULT,
    targetingCountry: country,
    targetingState: state,
    targetingCity: city,
    targetingZip: zip,
    targetingAsn: normalizeTargetingAsn(prefill.asn),
    qaLocationSource: QA_LOCATION_SOURCE_IDENTITY,
  };
  setForm((current) => ({
    ...current,
    ...formSnapshot,
  }));
  setSelectedMeta({
    state: state !== QA_TARGETING_ALL_STATES
      ? {
        providerCode: state,
        displayName: prefill.stateLabel
          || formatUsStateLabel(prefill.parsed?.stateAbbr || state),
      }
      : null,
    city: city !== QA_TARGETING_ALL_CITIES
      ? {
        providerCode: city,
        displayName: prefill.cityLabel || prefill.parsed?.city || city,
      }
      : null,
    zip: zip !== QA_TARGETING_ALL_ZIPS
      ? {
        providerCode: zip,
        displayName: zip,
        availableIpCount: zipIpCount == null ? null : Number(zipIpCount),
        providerSupported: zipIpCount == null && zipSupported == null
          ? undefined
          : (zipSupported !== false && Number(zipIpCount || 0) > 0),
      }
      : null,
    asn: null,
  });
  return { unsupportedZip, zip, formSnapshot };
}

function usesPaidTargetFilters(form) {
  if (form.targetingMode !== QA_TARGETING_MODE_FILTERS) return false;
  return (
    form.targetingState !== QA_TARGETING_ALL_STATES
    || form.targetingCity !== QA_TARGETING_ALL_CITIES
    || form.targetingZip !== QA_TARGETING_ALL_ZIPS
    || form.targetingAsn !== QA_TARGETING_ALL_ASNS
  );
}

function specificValue(value, allValue) {
  const raw = String(value || '').trim();
  if (!raw || raw === allValue || raw.toLowerCase() === 'all') return '';
  return raw;
}

/** Fresh nearby results rank first; summary fallback entries fill in the rest. */
function mergeNearbySuggestions(fetched, fromSummary) {
  const merged = [];
  const seen = new Set();
  [...(fetched || []), ...(fromSummary || [])].forEach((item) => {
    const key = String(item?.zip || item?.label || '').trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  });
  return merged;
}

function nearbySuggestionLabel(item) {
  if (!item) return '';
  return item.label
    || formatCompactQaLocationLabel({
      state: item.stateProviderCode || item.state,
      city: item.city,
      zip: item.zip,
    })
    || String(item.zip || '');
}

export default function ConfigureAuthorizedQaProxyModal({
  open,
  clientId,
  agencyId,
  profile,
  proxySummary,
  onClose,
  onSaved,
  canConfigureAsn = true,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingIdentity, setLoadingIdentity] = useState(false);
  const [locationOptions, setLocationOptions] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [catalogMeta, setCatalogMeta] = useState(null);
  const [selectedStateMeta, setSelectedStateMeta] = useState(null);
  const [selectedCityMeta, setSelectedCityMeta] = useState(null);
  const [selectedZipMeta, setSelectedZipMeta] = useState(null);
  const [selectedAsnMeta, setSelectedAsnMeta] = useState(null);
  const [resolvedSummary, setResolvedSummary] = useState(
    () => normalizeAuthorizedQaProxySummary(proxySummary),
  );
  const [resetToPersonalAddress, setResetToPersonalAddress] = useState(false);
  const [nearbySelectedZip, setNearbySelectedZip] = useState('');
  const [nearbyConfirmOpen, setNearbyConfirmOpen] = useState(false);
  const [persistingSelection, setPersistingSelection] = useState(false);
  const [nearbyResult, setNearbyResult] = useState(null);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [nearbyError, setNearbyError] = useState('');
  const [nearbyReloadToken, setNearbyReloadToken] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [switchingProvider, setSwitchingProvider] = useState(false);
  const [providerSwitchMessage, setProviderSwitchMessage] = useState('');
  const [providerSwitchError, setProviderSwitchError] = useState('');
  const resolvedSummaryRef = useRef(resolvedSummary);
  const identityPersistAttemptedRef = useRef(false);
  resolvedSummaryRef.current = resolvedSummary;

  const credentialsConfigured = Boolean(
    resolvedSummary?.credentialsConfigured
    || resolvedSummary?.providerCredentialsConfigured
    || resolvedSummary?.credentialSaved,
  );
  const showCostNotice = usesPaidTargetFilters(form);
  const webAccessMode = resolvedSummary?.webAccessMode || 'full_web';
  const assignmentExists = Boolean(resolvedSummary?.id);
  const selectedProvider = (assignmentExists ? resolvedSummary?.defaultProvider : form.provider)
    || 'dataimpulse';
  const selectedProviderLabel = formatQaProxyProviderLabel(selectedProvider);
  // The first save creates the assignment with the chosen provider; later saves keep it.
  const newAssignmentProvider = assignmentExists ? undefined : selectedProvider;
  const providerChoices = Array.isArray(resolvedSummary?.availableProviders)
    ? resolvedSummary.availableProviders
    : null;

  const handleProviderChange = async (event) => {
    const nextProvider = String(event.target.value || '');
    setProviderSwitchMessage('');
    setProviderSwitchError('');
    if (!nextProvider || nextProvider === selectedProvider) return;
    if (!assignmentExists) {
      setForm((current) => ({ ...current, provider: nextProvider }));
      return;
    }
    if (!clientId || !agencyId || !profile?.id) return;
    setSwitchingProvider(true);
    try {
      const saved = await setAuthorizedQaProxyProvider({
        clientId,
        agencyId,
        profileId: profile.id,
        provider: nextProvider,
      });
      setResolvedSummary(saved);
      setForm((current) => ({ ...current, provider: saved?.defaultProvider || nextProvider }));
      setProviderSwitchMessage(saved?.providerChangeMessage || QA_PROXY_PROVIDER_SWITCH_MESSAGE);
      onSaved?.(saved);
    } catch (err) {
      setProviderSwitchError(err?.message || 'Unable to change the QA proxy provider.');
    } finally {
      setSwitchingProvider(false);
    }
  };
  const selectionRequired = resolvedSummary?.providerTargetReady === false
    || String(resolvedSummary?.providerTargetStatus || '').toLowerCase() === 'selection_required'
    || Boolean(resolvedSummary?.locationSelectionRequired?.required);
  const selectionFallback = resolvedSummary?.locationSelectionRequired?.fallback || null;
  // 911Proxy targets the state only; the DataImpulse ZIP catalog never gates its saves.
  const stateOnlyProvider = selectedProvider === QA_PROXY_PROVIDER_911PROXY;
  const formZipUnsupported = !stateOnlyProvider
    && zipMetaUnsupported(selectedZipMeta, form.targetingZip);
  const showNearbyRecovery = !stateOnlyProvider && (selectionRequired || formZipUnsupported);

  const requestedTargeting = resolvedSummary?.locationSelectionRequired?.requestedTargeting
    || resolvedSummary?.qaRequestedTargeting
    || resolvedSummary?.qaOriginalRequestedTargeting
    || null;
  const formTargetingLabel = formatCompactQaLocationLabel({
    country: form.targetingCountry,
    state: form.targetingState,
    city: form.targetingCity,
    zip: form.targetingZip,
  });
  const requestedSummaryLabel = formatCompactQaLocationLabel(requestedTargeting)
    || formTargetingLabel
    || 'Location selection required';
  const savedLocationLabel = (() => {
    const label = formatQaTestLocationSummary(resolvedSummary);
    if (label && !/default/i.test(label)) return label;
    return formTargetingLabel || 'Not set yet';
  })();

  const nearbyCountry = specificValue(requestedTargeting?.country, '')
    || normalizeTargetingCountry(form.targetingCountry);
  const nearbyState = specificValue(requestedTargeting?.state, QA_TARGETING_ALL_STATES)
    || specificValue(form.targetingState, QA_TARGETING_ALL_STATES);
  const nearbyCity = specificValue(requestedTargeting?.city, QA_TARGETING_ALL_CITIES)
    || specificValue(form.targetingCity, QA_TARGETING_ALL_CITIES);
  const nearbyZip = specificValue(requestedTargeting?.zip, QA_TARGETING_ALL_ZIPS)
    || specificValue(form.targetingZip, QA_TARGETING_ALL_ZIPS);

  const nearbySuggestions = mergeNearbySuggestions(
    nearbyResult?.suggestions,
    selectionFallback?.nearbyProviderSupportedLocations,
  );
  const nearbySelection = nearbySuggestions.find((item) => item.zip === nearbySelectedZip) || null;
  const stateLevelTargeting = selectionFallback?.stateLevelTargeting
    || nearbyResult?.fallback?.stateLevelTargeting
    || (nearbyResult?.stateLevelSupported && nearbyResult?.stateProviderCode
      ? { country: nearbyCountry || 'us', state: nearbyResult.stateProviderCode }
      : null);
  const stateLevelLabel = formatUsStateLabel(
    stateLevelTargeting?.state || nearbyResult?.stateProviderCode || nearbyState,
  );

  const setSelectedMeta = useCallback(({ state, city, zip, asn }) => {
    setSelectedStateMeta(state);
    setSelectedCityMeta(city);
    setSelectedZipMeta(zip);
    setSelectedAsnMeta(asn);
  }, []);

  const persistUnsupportedRequestedLocation = useCallback(async (formSnapshot, extras = {}) => {
    if (!clientId || !agencyId || !profile?.id || !formSnapshot) return null;
    setPersistingSelection(true);
    try {
      const saved = await upsertAuthorizedQaProxy({
        clientId,
        agencyId,
        profileId: profile.id,
        provider: newAssignmentProvider,
        quotaBytes: extras.quotaBytes ?? '',
        status: extras.status || 'active',
        targetingMode: formSnapshot.targetingMode,
        targetingCountry: formSnapshot.targetingCountry,
        targetingState: formSnapshot.targetingState,
        targetingCity: formSnapshot.targetingCity,
        targetingZip: formSnapshot.targetingZip,
        targetingAsn: QA_TARGETING_ALL_ASNS,
        qaBrowserDisplay: extras.qaBrowserDisplay || QA_BROWSER_DISPLAY_CURRENT,
        qaLocationSource: QA_LOCATION_SOURCE_IDENTITY,
        resetToPersonalAddress: true,
      });
      if (saved?.password || saved?.username) {
        throw new Error('Server response included a secret and was rejected.');
      }
      setResolvedSummary(saved);
      setForm(formFromSummary(saved));
      setResetToPersonalAddress(false);
      onSaved?.(saved);
      return saved;
    } finally {
      setPersistingSelection(false);
    }
  }, [agencyId, clientId, newAssignmentProvider, onSaved, profile?.id]);

  const resolveIdentityLocation = useCallback(async ({ forceReset = false } = {}) => {
    if (!clientId || !agencyId) return;
    setLoadingIdentity(true);
    setError('');
    try {
      const prefill = await getAuthorizedQaProxyLocationFromIdentity({
        clientId,
        agencyId,
      });
      const applied = applyIdentityPrefill(prefill, setForm, setSelectedMeta);
      if (forceReset || applied.unsupportedZip) {
        setResetToPersonalAddress(true);
      }
      const summary = resolvedSummaryRef.current;
      const alreadySelectionRequired = summary?.providerTargetReady === false
        || String(summary?.providerTargetStatus || '').toLowerCase() === 'selection_required';
      const alreadyRequestedZip = String(
        summary?.qaRequestedTargeting?.zip
        || summary?.locationSelectionRequired?.requestedTargeting?.zip
        || '',
      ).trim();
      const sameUnsupportedAlreadyPersisted = alreadySelectionRequired
        && applied.unsupportedZip
        && alreadyRequestedZip
        && alreadyRequestedZip === String(applied.zip || '').trim();

      if (
        applied.unsupportedZip
        && profile?.id
        && (forceReset || (!sameUnsupportedAlreadyPersisted && !identityPersistAttemptedRef.current))
      ) {
        identityPersistAttemptedRef.current = true;
        try {
          await persistUnsupportedRequestedLocation(applied.formSnapshot);
        } catch (persistErr) {
          identityPersistAttemptedRef.current = false;
          setError(
            persistErr?.message
            || 'Unable to save requested Personal Address location for nearby selection.',
          );
        }
      }
    } catch (err) {
      if (forceReset) {
        setError(err.message || 'Unable to reset to personal address.');
      }
    } finally {
      setLoadingIdentity(false);
    }
  }, [
    agencyId,
    clientId,
    persistUnsupportedRequestedLocation,
    profile?.id,
    setSelectedMeta,
  ]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setError('');
    setResetToPersonalAddress(false);
    setNearbySelectedZip('');
    setNearbyConfirmOpen(false);
    setNearbyResult(null);
    setNearbyError('');
    setInfoOpen(false);
    setAdvancedOpen(false);
    identityPersistAttemptedRef.current = false;
    const initialSummary = normalizeAuthorizedQaProxySummary(proxySummary);
    setResolvedSummary(initialSummary);
    setForm(formFromSummary(proxySummary));
    setSelectedMeta({ state: null, city: null, zip: null, asn: null });

    const loadFreshSummary = async () => {
      if (!clientId || !agencyId || !profile?.id) return initialSummary;
      setLoadingSummary(true);
      try {
        const fresh = await getAuthorizedQaProxy({
          clientId,
          agencyId,
          profileId: profile.id,
        });
        if (cancelled || !fresh) return initialSummary;
        setResolvedSummary(fresh);
        setForm(formFromSummary(fresh));
        return fresh;
      } catch (err) {
        if (!cancelled) {
          if (!proxySummary?.credentialSaved && !proxySummary?.credentialsConfigured) {
            setError(err.message || 'Unable to load saved proxy configuration.');
          }
        }
        return initialSummary;
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };

    loadFreshSummary().then((summary) => {
      if (cancelled) return;
      if (!shouldPrefillFromIdentity(summary || initialSummary)) return;
      resolveIdentityLocation().catch(() => {
        // Optional prefill — ignore when identity address is missing.
      });
    });
    return () => {
      cancelled = true;
    };
    // Intentionally omit resolveIdentityLocation from deps: open/proxySummary drive
    // one load+prefill cycle per modal open; identity persist is guarded by refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proxySummary, clientId, agencyId, profile?.id, setSelectedMeta]);

  useEffect(() => {
    if (!open || !agencyId) return undefined;
    let cancelled = false;
    const loadOptions = async () => {
      setLoadingLocations(true);
      try {
        const [options, catalog] = await Promise.all([
          getAuthorizedQaProxyLocationOptions({
            agencyId,
            country: form.targetingCountry || 'us',
          }),
          getAuthorizedQaProxyTargetingCatalog({ agencyId }).catch(() => null),
        ]);
        if (cancelled) return;
        setLocationOptions({ ...options, notes: [] });
        const status = normalizeTargetingCatalogPayload(catalog);
        setCatalogMeta(status.imported ? status.catalog : null);
      } catch (err) {
        if (!cancelled) {
          setLocationOptions(null);
          setError(err.message || 'Unable to load location options.');
        }
      } finally {
        if (!cancelled) setLoadingLocations(false);
      }
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [open, agencyId, form.targetingCountry]);

  // Nearby recovery is loaded independently of the saved summary fallback so the
  // list is present even when the server payload has no cached suggestions.
  useEffect(() => {
    if (!open || !showNearbyRecovery) return undefined;
    if (!clientId || !agencyId) return undefined;
    let cancelled = false;
    setLoadingNearby(true);
    setNearbyError('');
    getAuthorizedQaProxyNearbyLocations({
      clientId,
      agencyId,
      profileId: profile?.id,
      country: nearbyCountry,
      state: nearbyState,
      city: nearbyCity,
      zip: nearbyZip,
      limit: 5,
    }).then((payload) => {
      if (cancelled) return;
      setNearbyResult(payload);
    }).catch((err) => {
      if (cancelled) return;
      setNearbyError(err?.message || 'Unable to load nearby provider-supported locations.');
    }).finally(() => {
      if (!cancelled) setLoadingNearby(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    showNearbyRecovery,
    clientId,
    agencyId,
    profile?.id,
    nearbyCountry,
    nearbyState,
    nearbyCity,
    nearbyZip,
    nearbyReloadToken,
  ]);

  useEffect(() => {
    if (!infoOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setInfoOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [infoOpen]);

  const searchStates = useCallback(
    (q) => searchAuthorizedQaProxyTargetingStates({ agencyId, q }),
    [agencyId],
  );
  const searchCities = useCallback(
    (q) => searchAuthorizedQaProxyTargetingCities({ agencyId, q }),
    [agencyId],
  );
  const searchZips = useCallback(
    (q) => searchAuthorizedQaProxyTargetingZips({ agencyId, q }),
    [agencyId],
  );
  const searchAsns = useCallback(
    (q) => Promise.resolve(searchAuthorizedQaProxyTargetingAsns({
      asns: locationOptions?.asns || [],
      q,
    })),
    [locationOptions],
  );

  if (!open || !profile) return null;

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'targetingMode' && value === QA_TARGETING_MODE_DEFAULT) {
        next.targetingCountry = 'us';
        next.targetingState = QA_TARGETING_ALL_STATES;
        next.targetingCity = QA_TARGETING_ALL_CITIES;
        next.targetingZip = QA_TARGETING_ALL_ZIPS;
        next.targetingAsn = QA_TARGETING_ALL_ASNS;
        next.qaLocationSource = QA_LOCATION_SOURCE_DEFAULT;
        setSelectedStateMeta(null);
        setSelectedCityMeta(null);
        setSelectedZipMeta(null);
        setSelectedAsnMeta(null);
      }
      if (field === 'targetingCountry') {
        next.targetingState = QA_TARGETING_ALL_STATES;
        next.targetingCity = QA_TARGETING_ALL_CITIES;
        next.targetingZip = QA_TARGETING_ALL_ZIPS;
        setSelectedStateMeta(null);
        setSelectedCityMeta(null);
        setSelectedZipMeta(null);
      }
      if (
        field === 'targetingMode'
        || field === 'targetingCountry'
        || field === 'targetingState'
        || field === 'targetingCity'
        || field === 'targetingZip'
        || field === 'targetingAsn'
      ) {
        if (field !== 'targetingMode' || value === QA_TARGETING_MODE_FILTERS) {
          next.qaLocationSource = QA_LOCATION_SOURCE_MANUAL;
          setResetToPersonalAddress(false);
        }
      }
      return next;
    });
  };

  const handleCatalogSelect = (field, setMeta, allValue) => (code, item) => {
    setMeta(item);
    setResetToPersonalAddress(false);
    setForm((current) => ({
      ...current,
      [field]: code,
      targetingMode: code === allValue ? current.targetingMode : QA_TARGETING_MODE_FILTERS,
      qaLocationSource: QA_LOCATION_SOURCE_MANUAL,
    }));
  };

  const handleResetToPersonalAddress = async () => {
    await resolveIdentityLocation({ forceReset: true });
  };

  const handleRetryNearby = () => {
    setNearbyError('');
    setNearbyReloadToken((current) => current + 1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const zipCode = String(form.targetingZip || '').trim();
      const zipIsSpecific = zipCode
        && zipCode !== QA_TARGETING_ALL_ZIPS
        && zipCode.toLowerCase() !== 'all';
      const zipUnsupported = !stateOnlyProvider
        && zipIsSpecific
        && zipMetaUnsupported(selectedZipMeta, zipCode);
      const saved = await upsertAuthorizedQaProxy({
        clientId,
        agencyId,
        profileId: profile.id,
        provider: newAssignmentProvider,
        quotaBytes: form.quotaBytes,
        status: form.status,
        targetingMode: form.targetingMode,
        targetingCountry: form.targetingCountry,
        targetingState: form.targetingState,
        targetingCity: form.targetingCity,
        targetingZip: form.targetingZip,
        targetingAsn: canConfigureAsn ? form.targetingAsn : QA_TARGETING_ALL_ASNS,
        qaBrowserDisplay: form.qaBrowserDisplay,
        qaLocationSource: form.qaLocationSource,
        resetToPersonalAddress: resetToPersonalAddress
          || (zipUnsupported && form.qaLocationSource === QA_LOCATION_SOURCE_IDENTITY),
      });
      if (saved?.password || saved?.username) {
        throw new Error('Server response included a secret and was rejected.');
      }
      setResolvedSummary(saved);
      onSaved?.(saved);
      // Stay open when selection is still required so nearby choices are visible.
      const savedStateOnly = (saved?.defaultProvider || selectedProvider) === QA_PROXY_PROVIDER_911PROXY;
      if (savedStateOnly ? saved?.launchTargetReady === false : (
        saved?.providerTargetReady === false
        || String(saved?.providerTargetStatus || '').toLowerCase() === 'selection_required'
      )) {
        setError('');
        setResetToPersonalAddress(false);
        return;
      }
      onClose?.();
    } catch (err) {
      const message = String(err?.message || '');
      if (
        message === 'Server response included a secret and was rejected.'
        || message.startsWith('Not available')
        || message.includes('unsupported_provider_target')
        || message.includes('does not currently list')
      ) {
        setError(
          message.includes('unsupported_provider_target') || message.includes('does not currently list')
            ? `${form.targetingZip || 'That ZIP'} is not currently available from the proxy provider. `
              + 'Choose a nearby provider-supported location.'
            : message,
        );
      } else {
        setError(message || PROXY_SAVE_FAILED_MESSAGE);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleApplyNearbySuggestion = async (suggestion) => {
    if (!suggestion?.targeting && !suggestion?.zip) return;
    setSaving(true);
    setError('');
    try {
      const targeting = suggestion.targeting || {};
      const saved = await upsertAuthorizedQaProxy({
        clientId,
        agencyId,
        profileId: profile.id,
        provider: newAssignmentProvider,
        quotaBytes: form.quotaBytes,
        status: form.status,
        targetingMode: QA_TARGETING_MODE_FILTERS,
        targetingCountry: targeting.country || form.targetingCountry || 'us',
        targetingState: targeting.state || suggestion.stateProviderCode || form.targetingState,
        targetingCity: targeting.city || QA_TARGETING_ALL_CITIES,
        targetingZip: targeting.zip || suggestion.zip,
        targetingAsn: QA_TARGETING_ALL_ASNS,
        qaBrowserDisplay: form.qaBrowserDisplay,
        qaLocationSource: QA_LOCATION_SOURCE_MANUAL,
        resetToPersonalAddress: false,
      });
      if (saved?.password || saved?.username) {
        throw new Error('Server response included a secret and was rejected.');
      }
      setNearbyConfirmOpen(false);
      setResolvedSummary(saved);
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setNearbyConfirmOpen(false);
      setError(err?.message || PROXY_SAVE_FAILED_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyStateLevel = async () => {
    if (!stateLevelTargeting) return;
    setSaving(true);
    setError('');
    try {
      const saved = await upsertAuthorizedQaProxy({
        clientId,
        agencyId,
        profileId: profile.id,
        provider: newAssignmentProvider,
        quotaBytes: form.quotaBytes,
        status: form.status,
        targetingMode: QA_TARGETING_MODE_FILTERS,
        targetingCountry: stateLevelTargeting.country || 'us',
        targetingState: stateLevelTargeting.state || form.targetingState,
        targetingCity: QA_TARGETING_ALL_CITIES,
        targetingZip: QA_TARGETING_ALL_ZIPS,
        targetingAsn: QA_TARGETING_ALL_ASNS,
        qaBrowserDisplay: form.qaBrowserDisplay,
        qaLocationSource: QA_LOCATION_SOURCE_MANUAL,
        resetToPersonalAddress: false,
      });
      if (saved?.password || saved?.username) {
        throw new Error('Server response included a secret and was rejected.');
      }
      setResolvedSummary(saved);
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      setError(err?.message || PROXY_SAVE_FAILED_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const countries = locationOptions?.countries || [{ code: 'us', label: 'United States' }];
  const catalogReady = Boolean(catalogMeta);
  const unavailableZipCode = showNearbyRecovery
    ? (specificValue(requestedTargeting?.zip, QA_TARGETING_ALL_ZIPS)
      || specificValue(form.targetingZip, QA_TARGETING_ALL_ZIPS))
    : '';
  const catalogZipSupported = Boolean(
    selectedZipMeta
    && Number(selectedZipMeta.availableIpCount || 0) > 0
    && selectedZipMeta.providerSupported !== false
    && !formZipUnsupported,
  );
  const nearbySelectionRequiredCta = showNearbyRecovery && !catalogZipSupported;
  const busy = saving || persistingSelection;

  const resetPersonalAddressButton = (
    <button
      type="button"
      className="fg-btn fg-btn-light fg-btn-sm"
      data-testid="authorized-qa-proxy-reset-personal-address"
      onClick={handleResetToPersonalAddress}
      disabled={loadingIdentity || busy}
    >
      {loadingIdentity ? 'Resolving…' : 'Reset to Personal Address'}
    </button>
  );

  const stateLevelButton = stateLevelTargeting ? (
    <button
      type="button"
      className="fg-btn fg-btn-light fg-btn-sm"
      data-testid="authorized-qa-proxy-use-state-level"
      disabled={busy}
      onClick={handleApplyStateLevel}
    >
      {`Use ${stateLevelLabel || 'state'} state-level`}
    </button>
  ) : null;

  return (
    <div className="fg-modal-backdrop is-open" data-testid="configure-authorized-qa-proxy-modal">
      <div
        className="fg-modal fg-browser-profile-modal fg-qa-modal-compact"
        role="dialog"
        aria-modal="true"
        aria-label="Configure QA Browser"
      >
        <button type="button" className="fg-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <div className="fg-modal-header fg-qa-modal-compact__header">
          <div className="fg-modal-icon">QA</div>
          <div className="fg-qa-modal-compact__heading">
            <h2>Configure QA Browser</h2>
            <p>Choose the QA testing location for this client.</p>
          </div>
          <div className="fg-qa-info">
            <button
              type="button"
              className="fg-qa-info__button"
              data-testid="authorized-qa-proxy-info-button"
              aria-expanded={infoOpen}
              aria-label="How QA browsing works"
              onClick={() => setInfoOpen((current) => !current)}
            >
              ⓘ
            </button>
            {infoOpen ? (
              <div
                className="fg-qa-info-popover"
                data-testid="authorized-qa-proxy-info-popover"
                role="note"
              >
                <ul>
                  <li>Primary Browser stays direct — never proxied.</li>
                  <li>QA Browser sessions are always proxied.</li>
                  <li>The client&apos;s Personal Address is never changed.</li>
                  <li>If the ZIP is unavailable, pick a nearby supported location.</li>
                  <li>Public web is proxied; private networks stay blocked.</li>
                  <li>{`Provider: ${selectedProviderLabel} — Managed by Admin.`}</li>
                  <li>{`Policy: ${formatWebAccessModeLabel(webAccessMode)}.`}</li>
                </ul>
              </div>
            ) : null}
          </div>
        </div>

        <form className="fg-profile-form fg-qa-modal-compact__form" noValidate onSubmit={handleSubmit}>
          <div className="fg-qa-modal-compact__status">
            <p
              className="fg-qa-modal-compact__status-row"
              data-testid="authorized-qa-proxy-provider-status"
            >
              <span className="fg-browser-search-helper__label">Provider</span>
              <span data-testid="authorized-qa-proxy-provider-label">
                {(providerChoices?.find((row) => row.id === selectedProvider)?.configured ?? credentialsConfigured) ? (
                  <span data-testid="authorized-qa-proxy-provider-configured">
                    {`✓ ${selectedProviderLabel} — Managed by Admin · Configured by administrator`}
                  </span>
                ) : (
                  <span data-testid="authorized-qa-proxy-provider-missing">
                    {`${selectedProviderLabel} — Admin must configure ${selectedProviderLabel} Provider`}
                  </span>
                )}
              </span>
            </p>
            {providerChoices ? (
              <label
                className="fg-qa-provider-field"
                htmlFor="authorized-qa-proxy-default-provider"
                data-testid="authorized-qa-proxy-default-provider-row"
              >
                <span className="fg-qa-provider-field__label">Default Provider</span>
                <select
                  id="authorized-qa-proxy-default-provider"
                  className="fg-application-input fg-application-select fg-qa-provider-field__select"
                  value={selectedProvider}
                  onChange={handleProviderChange}
                  disabled={switchingProvider || saving}
                  data-testid="authorized-qa-proxy-default-provider"
                >
                  {AUTHORIZED_QA_PROXY_PROVIDERS.map((item) => {
                    const choice = providerChoices.find((row) => row.id === item.id);
                    const unavailable = item.id !== selectedProvider && !choice?.active;
                    return (
                      <option key={item.id} value={item.id} disabled={unavailable}>
                        {unavailable ? `${item.label} (not enabled by admin)` : item.label}
                      </option>
                    );
                  })}
                </select>
              </label>
            ) : null}
            {providerSwitchMessage ? (
              <p className="fg-field-hint" role="status" data-testid="authorized-qa-proxy-provider-switch-message">
                {providerSwitchMessage}
              </p>
            ) : null}
            {providerSwitchError ? (
              <p className="fg-field-error" role="alert" data-testid="authorized-qa-proxy-provider-switch-error">
                {providerSwitchError}
              </p>
            ) : null}
            {selectedProvider === QA_PROXY_PROVIDER_911PROXY ? (
              <p className="fg-field-hint" data-testid="authorized-qa-proxy-provider-targeting-note">
                911Proxy uses state targeting only. City and ZIP stay saved on this profile but are not sent to 911Proxy.
              </p>
            ) : null}
            <p
              className="fg-qa-modal-compact__status-row"
              data-testid="authorized-qa-proxy-policy-status"
            >
              <span className="fg-browser-search-helper__label">Policy</span>
              <span className="fg-field-hint" data-testid="authorized-qa-proxy-policy-label">
                {`${formatWebAccessModeLabel(webAccessMode)} — Managed by Admin`}
              </span>
            </p>
            {loadingSummary ? (
              <p className="fg-field-hint" data-testid="authorized-qa-proxy-loading-summary">
                Loading saved configuration…
              </p>
            ) : null}
          </div>

          <div className="fg-form-grid">
            <div className="fg-form-span-2" data-testid="authorized-qa-proxy-qa-test-location">
              <span className="fg-browser-search-helper__label">QA Test Location</span>
              <p className="fg-field-hint" data-testid="authorized-qa-proxy-location-source">
                {`${savedLocationLabel} · Source: ${formatQaLocationSourceLabel(form.qaLocationSource)}`}
              </p>
              {catalogReady ? (
                <p className="fg-field-hint" data-testid="authorized-qa-proxy-location-hint">
                  {stateOnlyProvider
                    ? 'Choose a State for this QA session.'
                    : 'Choose a State, City, or ZIP code for this QA session.'}
                </p>
              ) : (
                <p className="fg-field-hint" data-testid="authorized-qa-proxy-catalog-missing">
                  Location catalog is not available. Contact your administrator.
                </p>
              )}
              {loadingLocations || loadingIdentity ? (
                <p className="fg-field-hint" data-testid="authorized-qa-proxy-location-loading">
                  {loadingIdentity ? 'Resolving personal address…' : 'Loading location options…'}
                </p>
              ) : null}

              <div className="fg-form-grid">
                <label>
                  <span>Country</span>
                  <select
                    className="fg-application-input"
                    value={form.targetingCountry}
                    data-testid="authorized-qa-proxy-targeting-country"
                    onChange={handleChange('targetingCountry')}
                  >
                    {countries.map((option) => (
                      <option key={option.code} value={option.code}>{option.label}</option>
                    ))}
                  </select>
                </label>

                {catalogReady ? (
                  <>
                    <QaTargetingCatalogSearch
                      label="State"
                      kind="state"
                      testId="authorized-qa-proxy-targeting-state"
                      value={form.targetingState}
                      allValue={QA_TARGETING_ALL_STATES}
                      allLabel="All states"
                      placeholder="Search state"
                      selectedItem={selectedStateMeta}
                      onSearch={searchStates}
                      onSelect={handleCatalogSelect(
                        'targetingState',
                        setSelectedStateMeta,
                        QA_TARGETING_ALL_STATES,
                      )}
                    />
                  </>
                ) : null}

                {catalogReady && !stateOnlyProvider ? (
                  <>
                    <QaTargetingCatalogSearch
                      label="City"
                      kind="city"
                      testId="authorized-qa-proxy-targeting-city"
                      value={form.targetingCity}
                      allValue={QA_TARGETING_ALL_CITIES}
                      allLabel="All cities"
                      placeholder="Search city"
                      selectedItem={selectedCityMeta}
                      onSearch={searchCities}
                      onSelect={handleCatalogSelect(
                        'targetingCity',
                        setSelectedCityMeta,
                        QA_TARGETING_ALL_CITIES,
                      )}
                    />

                    <QaTargetingCatalogSearch
                      label="ZIP code"
                      kind="zip"
                      testId="authorized-qa-proxy-targeting-zip"
                      value={form.targetingZip}
                      allValue={QA_TARGETING_ALL_ZIPS}
                      allLabel="All ZIP codes"
                      placeholder="Search ZIP code"
                      selectedItem={selectedZipMeta}
                      emptyMessage={
                        /^\d{5}$/.test(String(form.targetingZip || '').trim())
                          ? `${form.targetingZip} is not currently available from the proxy provider.`
                          : 'No matching provider-supported ZIP codes.'
                      }
                      onSearch={searchZips}
                      onSelect={handleCatalogSelect(
                        'targetingZip',
                        setSelectedZipMeta,
                        QA_TARGETING_ALL_ZIPS,
                      )}
                    />
                    <p
                      className="fg-field-hint fg-form-span-2"
                      data-testid="authorized-qa-proxy-catalog-availability-hint"
                    >
                      Provider catalog availability. This does not verify the location of the
                      current proxy exit.
                    </p>
                  </>
                ) : null}
              </div>

              {unavailableZipCode ? (
                <p
                  className="fg-browser-proxy-warning"
                  data-testid="authorized-qa-proxy-zip-unavailable"
                >
                  {`⚠ ${unavailableZipCode} is not currently available from the proxy provider.`}
                </p>
              ) : null}

              {showNearbyRecovery ? (
                <div
                  className="fg-bp-location-fallback"
                  data-testid="authorized-qa-proxy-nearby-recovery"
                >
                  <strong>Nearest provider-supported locations</strong>
                  <p className="fg-field-hint">
                    {'Requested: '}
                    <span data-testid="authorized-qa-proxy-requested-summary">
                      {requestedSummaryLabel}
                    </span>
                    {' — from the client\u2019s Personal Address, which stays unchanged.'}
                  </p>

                  {loadingNearby ? (
                    <p className="fg-field-hint" data-testid="authorized-qa-proxy-nearby-loading">
                      Finding nearby locations...
                    </p>
                  ) : null}

                  {!loadingNearby && nearbySuggestions.length ? (
                    <fieldset
                      className="fg-bp-location-fallback__list"
                      data-testid="authorized-qa-proxy-nearby-list"
                    >
                        <legend>Choose a QA location</legend>
                      {nearbySuggestions.map((item) => {
                        const id = `qa-modal-nearby-${item.zip}`;
                        return (
                          <label
                            key={item.zip || item.label}
                            htmlFor={id}
                            className="fg-bp-location-fallback__option"
                          >
                            <input
                              id={id}
                              type="radio"
                              name="qa-modal-nearby-zip"
                              value={item.zip || ''}
                              checked={nearbySelectedZip === item.zip}
                              disabled={busy}
                              onChange={() => setNearbySelectedZip(item.zip)}
                              data-testid={`authorized-qa-proxy-nearby-option-${item.zip}`}
                            />
                            <span>
                              <strong>{nearbySuggestionLabel(item)}</strong>
                              <span className="fg-field-hint">
                                {[
                                  formatSuggestionDistanceHint(item),
                                  item.availableIpCount != null
                                    ? `${item.availableIpCount} provider-listed IPs`
                                    : null,
                                ].filter(Boolean).join(' · ')}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </fieldset>
                  ) : null}

                  {!loadingNearby && !nearbySuggestions.length ? (
                    <div data-testid="authorized-qa-proxy-nearby-empty">
                      <p className="fg-field-hint">
                        {nearbyError
                          || 'No nearby provider-supported locations were returned for this ZIP.'}
                      </p>
                      <p className="fg-field-hint">
                        Search the State, City, or ZIP catalog above to pick a supported QA
                        location manually.
                      </p>
                      <button
                        type="button"
                        className="fg-btn fg-btn-light fg-btn-sm"
                        data-testid="authorized-qa-proxy-retry-nearby"
                        disabled={busy || loadingNearby}
                        onClick={handleRetryNearby}
                      >
                        Retry
                      </button>
                    </div>
                  ) : null}

                  <div className="fg-bp-location-fallback__secondary">
                    {stateLevelButton}
                    {resetPersonalAddressButton}
                  </div>
                </div>
              ) : (
                <div className="fg-bp-location-fallback__secondary">
                  {resetPersonalAddressButton}
                </div>
              )}

              {showCostNotice && !stateOnlyProvider ? (
                <p
                  className="fg-field-hint"
                  data-testid="authorized-qa-proxy-targeted-cost-notice"
                >
                  {TARGETED_TRAFFIC_COST_NOTICE}
                </p>
              ) : null}
            </div>
          </div>

          <section className="fg-qa-advanced" data-testid="authorized-qa-proxy-advanced">
            <button
              type="button"
              className="fg-qa-advanced__toggle"
              aria-expanded={advancedOpen}
              data-testid="authorized-qa-proxy-advanced-toggle"
              onClick={() => setAdvancedOpen((current) => !current)}
            >
              {advancedOpen ? '▾ Advanced' : '▸ Advanced'}
            </button>

            <div className="fg-qa-advanced__body" hidden={!advancedOpen}>
              <div className="fg-form-grid">
                {canConfigureAsn && catalogReady ? (
                  <QaTargetingCatalogSearch
                    label="ASN"
                    kind="asn"
                    testId="authorized-qa-proxy-targeting-asn"
                    value={form.targetingAsn}
                    allValue={QA_TARGETING_ALL_ASNS}
                    allLabel="All ASNs"
                    placeholder="Search ASN"
                    selectedItem={selectedAsnMeta}
                    onSearch={searchAsns}
                    onSelect={handleCatalogSelect(
                      'targetingAsn',
                      setSelectedAsnMeta,
                      QA_TARGETING_ALL_ASNS,
                    )}
                  />
                ) : (
                  <div data-testid="authorized-qa-proxy-targeting-asn-static">
                    <span className="fg-browser-search-helper__label">ASN</span>
                    <p className="fg-field-hint">All ASNs</p>
                  </div>
                )}

                <label>
                  <span>Status</span>
                  <select
                    className="fg-application-input"
                    value={form.status === 'suspended' ? 'suspended' : 'active'}
                    data-testid="authorized-qa-proxy-status"
                    onChange={handleChange('status')}
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </label>

                <label>
                  <span>Monthly traffic allowance (bytes)</span>
                  <input
                    className="fg-application-input"
                    type="number"
                    min="0"
                    value={form.quotaBytes}
                    data-testid="authorized-qa-proxy-quota"
                    onChange={handleChange('quotaBytes')}
                  />
                  <span className="fg-field-hint">
                    Leave blank for no client-specific quota.
                  </span>
                </label>

                <label data-testid="authorized-qa-proxy-browser-display">
                  <span>QA browser display</span>
                  <select
                    className="fg-application-input"
                    value={normalizeQaBrowserDisplay(form.qaBrowserDisplay)}
                    onChange={handleChange('qaBrowserDisplay')}
                    data-testid="authorized-qa-proxy-browser-display-select"
                  >
                    {QA_BROWSER_DISPLAY_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                  <span className="fg-field-hint">
                    Current monitor follows the display where the launcher runs. Choose Monitor 2+
                    for a specific display.
                  </span>
                </label>

                <fieldset
                  className="fg-radio-fieldset fg-form-span-2"
                  data-testid="authorized-qa-proxy-targeting-mode"
                >
                  <legend>Targeting mode</legend>
                  <label>
                    <input
                      type="radio"
                      name="qa-targeting-mode"
                      value={QA_TARGETING_MODE_DEFAULT}
                      checked={form.targetingMode === QA_TARGETING_MODE_DEFAULT}
                      onChange={handleChange('targetingMode')}
                      data-testid="authorized-qa-proxy-targeting-default"
                    />
                    Default targeting
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="qa-targeting-mode"
                      value={QA_TARGETING_MODE_FILTERS}
                      checked={form.targetingMode === QA_TARGETING_MODE_FILTERS}
                      onChange={handleChange('targetingMode')}
                      data-testid="authorized-qa-proxy-targeting-filters"
                    />
                    Target filters
                  </label>
                </fieldset>
              </div>
            </div>
          </section>

          {error ? (
            <p className="fg-form-error" data-testid="authorized-qa-proxy-form-error">{error}</p>
          ) : null}

          {nearbyConfirmOpen ? null : (
            <div className="fg-modal-actions fg-modal-footer">
              <button type="button" className="fg-btn fg-btn-light" onClick={onClose}>
                Cancel
              </button>
              {nearbySelectionRequiredCta ? (
                <button
                  type="button"
                  className="fg-btn fg-btn-primary"
                  data-testid="authorized-qa-proxy-save"
                  disabled={
                    busy
                    || loadingNearby
                    || loadingSummary
                    || !nearbySelection
                  }
                  onClick={() => setNearbyConfirmOpen(true)}
                >
                  {loadingNearby
                    ? 'Finding nearby locations…'
                    : (nearbySelection ? `Use ${nearbySelection.zip}` : 'Select a location first')}
                </button>
              ) : (
                <button
                  type="submit"
                  className="fg-btn fg-btn-primary"
                  data-testid="authorized-qa-proxy-save"
                  disabled={busy || loadingSummary || formZipUnsupported}
                >
                  {saving ? 'Saving…' : 'Save QA Location'}
                </button>
              )}
            </div>
          )}
        </form>

        {nearbyConfirmOpen && nearbySelection ? (
          <div
            className="fg-qa-confirm-overlay"
            data-testid="authorized-qa-proxy-nearby-confirm"
            role="dialog"
            aria-modal="true"
            aria-label="Use this QA location?"
          >
            <div className="fg-qa-confirm-overlay__panel">
              <h3>Use this QA location?</h3>
              <dl className="fg-bp-location-fallback__confirm-grid">
                <dt>QA testing location</dt>
                <dd>{nearbySuggestionLabel(nearbySelection)}</dd>
                <dt>Proximity</dt>
                <dd>{formatSuggestionDistanceHint(nearbySelection)}</dd>
                <dt>Provider catalog</dt>
                <dd>
                  {nearbySelection.availableIpCount != null
                    ? `${nearbySelection.availableIpCount} provider-listed IPs · not yet verified`
                    : 'Listed by provider · not yet verified'}
                </dd>
                <dt>Original client location</dt>
                <dd>{selectionFallback?.clientLocationSummary || requestedSummaryLabel}</dd>
              </dl>
              <p className="fg-field-hint">
                This changes only the QA testing location. The client&apos;s Personal Address
                remains unchanged.
              </p>
              <div className="fg-bp-location-fallback__actions">
                <button
                  type="button"
                  className="fg-btn fg-btn-primary"
                  data-testid="authorized-qa-proxy-nearby-confirm-btn"
                  disabled={busy}
                  onClick={() => handleApplyNearbySuggestion(nearbySelection)}
                >
                  {saving ? 'Saving…' : 'Confirm QA Location'}
                </button>
                <button
                  type="button"
                  className="fg-btn fg-btn-light"
                  disabled={busy}
                  onClick={() => setNearbyConfirmOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
